import { useEffect, useRef, useState, type ReactNode } from 'react';
import { UMIcon, UMR, UMToolbarIconButton, UMToolbarInput } from '../_components';
import { MEASURE_TOOLS } from './MeasureControls';
import type { GeoDisplayId, ToolGroupSpec, VariantId } from './types';

/**
 * Local copy of the production map toolbar (`UMMapToolbar`) with the reordered layout, the
 * classification proposals (A/B/C) and the geometry display proposals (G1-G4). Lives in the
 * prototype because `_components` is shared and reproduces production as-is. Sizes, colours and
 * icons come from `UMR` / home-map.md.
 *
 * Differences with production, decided 21/10: measures moved to the bottom controls, the
 * `more_vert` selection menu is dropped, the selection tools only appear with an active selection,
 * and the building-geometry tools of the rail panel are promoted to the bar.
 */

/** Selection action buttons: they act on the content of the active selection. */
export const TOOL_GROUPS: ToolGroupSpec[] = [
  {
    id: 'buildings',
    label: 'Bâtiments',
    icon: 'domain_add',
    tools: [
      { id: 'add-one', icon: 'domain_add', label: 'Ajouter un bâtiment' },
      { id: 'add-zone', icon: 'highlight_alt', label: 'Ajouter des bâtiments par zone' },
      { id: 'remove-one', icon: 'domain_disabled', label: 'Retirer un bâtiment' },
      { id: 'remove-zone', icon: 'remove_selection', label: 'Retirer des bâtiments par zone' },
    ],
  },
  {
    id: 'pipes',
    label: 'Réseaux',
    icon: 'polyline',
    tools: [
      { id: 'draw-pipe', icon: 'polyline', label: 'Tracer une canalisation' },
      { id: 'erase-pipe-one', icon: 'ink_eraser', label: 'Supprimer une canalisation' },
      { id: 'erase-pipe-zone', icon: 'remove_selection', label: 'Supprimer des canalisations par zone' },
    ],
  },
];

/**
 * Building-geometry tools promoted from the rail panel « Géométries des bâtiments »
 * (`UM_GEOMETRY_TOOLS`). They act on a building, not on the selection, hence their own family.
 */
export const GEOMETRY_GROUP: ToolGroupSpec = {
  id: 'geometries',
  label: 'Géométries',
  icon: 'shape_line',
  tools: [
    { id: 'geo-create', icon: 'linked_services', label: 'Créer un bâtiment' },
    { id: 'geo-edit', icon: 'rebase_edit', label: 'Modifier le tracé' },
    { id: 'geo-cut', icon: 'content_cut', label: 'Découper' },
    { id: 'geo-merge', icon: 'stack_group', label: 'Fusionner' },
    { id: 'geo-unmerge', icon: 'split_scene', label: 'Défusionner' },
  ],
};

/** The 9 buttons of the production bar, in their production order. */
const CURRENT_BAR_TOOLS = [...TOOL_GROUPS.flatMap((g) => g.tools), ...MEASURE_TOOLS];
const ALL_TOOLS = [...CURRENT_BAR_TOOLS, ...GEOMETRY_GROUP.tools];

export function toolLabel(id: string) {
  return ALL_TOOLS.find((t) => t.id === id)?.label ?? id;
}

/** Production rule (`UMGeometriesPanel`): everything but "Créer" needs a drawn or selected building. */
function geometryGroup(hasBuilding: boolean): ToolGroupSpec {
  return { ...GEOMETRY_GROUP, tools: GEOMETRY_GROUP.tools.map((t) => ({ ...t, disabled: t.id !== 'geo-create' && !hasBuilding })) };
}

const Divider = () => <span aria-hidden style={{ width: 1, height: 24, background: UMR.border, flexShrink: 0 }} />;

function ToolButton({ icon, label, active, disabled, onClick }: { icon: string; label: string; active: boolean; disabled?: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={disabled ? `${label} — sélectionnez d’abord un bâtiment` : label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: UMR.size.iconButton, height: UMR.size.iconButton, flexShrink: 0, border: 'none', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? UMR.blue : hover && !disabled ? UMR.hover : 'transparent',
      }}
    >
      <UMIcon name={icon} size={20} color={disabled ? UMR.textDisabled : active ? UMR.white : UMR.black} style={{ height: 24 }} />
    </button>
  );
}

/** Labelled dropdown used by variant B: "Bâtiments ▾" opening the tools of one family. */
function ToolMenu({ group, activeTool, onSelect }: { group: ToolGroupSpec; activeTool: string | null; onSelect: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasActive = group.tools.some((t) => t.id === activeTool);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: UMR.size.iconButton, display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px',
          border: `1px solid ${hasActive ? UMR.blue : UMR.borderInput}`, background: hasActive ? UMR.bluePressed : UMR.white,
          color: UMR.black, fontFamily: UMR.font, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <UMIcon name={hasActive ? ALL_TOOLS.find((t) => t.id === activeTool)!.icon : group.icon} size={20} color={hasActive ? UMR.blue : UMR.black} style={{ height: 24 }} />
        {group.label}
        <UMIcon name="keyboard_arrow_down" size={18} color={UMR.black} style={{ height: 24 }} />
      </button>
      {open && (
        <ul role="menu" style={{ position: 'absolute', top: UMR.size.iconButton + 2, left: 0, margin: 0, padding: '4px 0', listStyle: 'none', minWidth: 260, background: UMR.white, border: `1px solid ${UMR.border}`, boxShadow: 'rgba(0,0,0,0.1) 0 2px 8px', zIndex: 50 }}>
          {group.tools.map((t) => (
            <li key={t.id} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={activeTool === t.id}
                disabled={t.disabled}
                onClick={() => { onSelect(t.id); setOpen(false); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none',
                  background: activeTool === t.id ? UMR.bluePressed : 'transparent', cursor: t.disabled ? 'not-allowed' : 'pointer',
                  fontFamily: UMR.font, fontSize: 14, color: t.disabled ? UMR.textDisabled : UMR.black, textAlign: 'left',
                }}
              >
                <UMIcon name={t.icon} size={20} color={t.disabled ? UMR.textDisabled : UMR.black} style={{ height: 24 }} />
                {t.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Family selector of variant C (same look as the Fluid segmented control used in the panels). */
function FamilyPicker({ groups, value, onChange }: { groups: ToolGroupSpec[]; value: ToolGroupSpec['id']; onChange: (id: ToolGroupSpec['id']) => void }) {
  return (
    <div role="radiogroup" aria-label="Famille d’outils" style={{ display: 'flex', flexShrink: 0, border: `1px solid ${UMR.borderInput}`, height: UMR.size.iconButton }}>
      {groups.map((g) => {
        const active = g.id === value;
        return (
          <button
            key={g.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(g.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px', border: 'none', cursor: 'pointer',
              background: active ? UMR.blue : UMR.white, color: active ? UMR.white : UMR.black,
              fontFamily: UMR.font, fontSize: 14, whiteSpace: 'nowrap',
            }}
          >
            <UMIcon name={g.icon} size={18} color={active ? UMR.white : UMR.black} style={{ height: 24 }} />
            {g.label}
          </button>
        );
      })}
    </div>
  );
}

export interface MapToolbarProps {
  variant: VariantId;
  /** How the building-geometry tools are exposed (G1-G4). */
  geo: GeoDisplayId;
  /** A building is drawn or selected on the map: unlocks Modifier / Découper / Fusionner / Défusionner. */
  hasBuilding: boolean;
  /** G4 only: the bar is switched to geometry editing. */
  editMode: boolean;
  onEditModeChange: (value: boolean) => void;
  search: string;
  onSearchChange: (value: string) => void;
  selections: string[];
  selectionLabel: string;
  onSelectionChange: (value: string) => void;
  onCreateFolder: () => void;
  onCenterSelection: () => void;
  onSelectionActions: () => void;
  activeTool: string | null;
  onToolChange: (id: string | null) => void;
  family: ToolGroupSpec['id'];
  onFamilyChange: (id: ToolGroupSpec['id']) => void;
  onLocate: () => void;
}

export function MapToolbar({
  variant, geo, hasBuilding, editMode, onEditModeChange,
  search, onSearchChange, selections, selectionLabel, onSelectionChange,
  onCreateFolder, onCenterSelection, onSelectionActions,
  activeTool, onToolChange, family, onFamilyChange, onLocate,
}: MapToolbarProps) {
  const [open, setOpen] = useState(false);
  const toggleTool = (id: string) => onToolChange(activeTool === id ? null : id);

  const searchField = (
    <UMToolbarInput ariaLabel="Rechercher" placeholder="Rechercher" value={search} trailingIcon="search" onChange={onSearchChange} />
  );

  const selectionField = (
    <div style={{ display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding, flexShrink: 0 }}>
      <div style={{ position: 'relative' }}>
        <UMToolbarInput
          ariaLabel="Sélection"
          placeholder="Aucune sélection"
          value={selectionLabel}
          trailingIcon="keyboard_arrow_down"
          readOnly
          onClick={() => setOpen((v) => !v)}
        />
        {open && (
          <ul role="listbox" style={{ position: 'absolute', top: UMR.size.inputHeight, left: 0, width: UMR.size.inputWidth, margin: 0, padding: 0, listStyle: 'none', background: UMR.white, border: `1px solid ${UMR.border}`, zIndex: 50, fontSize: 14 }}>
            {selections.map((s) => (
              <li
                key={s}
                role="option"
                aria-selected={s === selectionLabel}
                onClick={() => { onSelectionChange(s === selectionLabel ? '' : s); setOpen(false); }}
                style={{ padding: '8px 16px', cursor: 'pointer', fontFamily: UMR.font }}
              >
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>
      {selectionLabel ? (
        <div style={{ display: 'flex' }}>
          <UMToolbarIconButton icon="center_focus_strong" label="Centrer sur ma sélection" onClick={onCenterSelection} />
          {/* The "more_vert" selection menu is being removed from the product — kept only in "Actuel". */}
          {variant === 'current' && (
            <UMToolbarIconButton icon="more_vert" label="Actions" title="Voir les actions" onClick={onSelectionActions} />
          )}
        </div>
      ) : (
        <UMToolbarIconButton icon="create_new_folder" label="Créer une sélection" onClick={onCreateFolder} />
      )}
    </div>
  );

  const geoGroup = geometryGroup(hasBuilding);
  /**
   * Groups rendered by the active variant. Selection tools need an active selection; the geometry
   * family joins them only in G1, and replaces them in G4 while edit mode is on.
   */
  const selectionGroups = selectionLabel ? TOOL_GROUPS : [];
  let barGroups: ToolGroupSpec[];
  if (geo === 'mode') barGroups = editMode ? [geoGroup] : selectionGroups;
  else if (geo === 'groupe') barGroups = [...selectionGroups, geoGroup];
  else barGroups = selectionGroups;

  const activeFamily = barGroups.find((g) => g.id === family) ?? barGroups[0];

  let tools: ReactNode;
  if (variant === 'current') {
    tools = (
      <div style={{ display: 'flex' }}>
        {CURRENT_BAR_TOOLS.map((t) => (
          <ToolButton key={t.id} icon={t.icon} label={t.label} active={activeTool === t.id} onClick={() => toggleTool(t.id)} />
        ))}
      </div>
    );
  } else if (barGroups.length === 0) {
    // Hypothesis under test: without an active selection the selection tools have nothing to act on.
    tools = (
      <span style={{ fontSize: 12, lineHeight: '16px', color: UMR.textSecondary, whiteSpace: 'nowrap' }}>
        {editMode ? 'Mode édition' : 'Choisissez une sélection pour afficher les outils'}
      </span>
    );
  } else if (variant === 'groupes') {
    tools = (
      <div style={{ display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding }}>
        {barGroups.map((g, i) => (
          <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding }}>
            {i > 0 && <Divider />}
            <div role="group" aria-label={g.label} style={{ display: 'flex' }}>
              {g.tools.map((t) => (
                <ToolButton key={t.id} icon={t.icon} label={`${g.label} · ${t.label}`} active={activeTool === t.id} disabled={t.disabled} onClick={() => toggleTool(t.id)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  } else if (variant === 'menus') {
    tools = (
      <div style={{ display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding }}>
        {barGroups.map((g) => (
          <ToolMenu key={g.id} group={g} activeTool={activeTool} onSelect={toggleTool} />
        ))}
      </div>
    );
  } else {
    tools = (
      <div style={{ display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding }}>
        <FamilyPicker groups={barGroups} value={activeFamily.id} onChange={(id) => { onFamilyChange(id); onToolChange(null); }} />
        <Divider />
        <div role="group" aria-label={activeFamily.label} style={{ display: 'flex' }}>
          {activeFamily.tools.map((t) => (
            <ToolButton key={t.id} icon={t.icon} label={t.label} active={activeTool === t.id} disabled={t.disabled} onClick={() => toggleTool(t.id)} />
          ))}
        </div>
      </div>
    );
  }

  /** G2 / G3 / G4 add their own control after the variant tools. */
  let geoControl: ReactNode = null;
  if (variant !== 'current' && geo === 'menu') {
    geoControl = <ToolMenu group={geoGroup} activeTool={activeTool} onSelect={toggleTool} />;
  } else if (variant !== 'current' && geo === 'contextuelle') {
    const create = geoGroup.tools[0];
    geoControl = (
      <div role="group" aria-label="Géométries" style={{ display: 'flex' }}>
        <ToolButton icon={create.icon} label={create.label} active={activeTool === create.id} onClick={() => toggleTool(create.id)} />
      </div>
    );
  } else if (variant !== 'current' && geo === 'mode') {
    geoControl = (
      <button
        type="button"
        aria-pressed={editMode}
        onClick={() => { onEditModeChange(!editMode); onToolChange(null); }}
        style={{
          height: UMR.size.iconButton, display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px', flexShrink: 0,
          border: `1px solid ${editMode ? UMR.blue : UMR.borderInput}`, background: editMode ? UMR.blue : UMR.white,
          color: editMode ? UMR.white : UMR.black, fontFamily: UMR.font, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <UMIcon name="shape_line" size={20} color={editMode ? UMR.white : UMR.black} style={{ height: 24 }} />
        Éditer les géométries
      </button>
    );
  }

  return (
    <div
      style={{
        pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: UMR.size.toolbarPadding,
        padding: UMR.size.toolbarPadding, background: UMR.white,
        borderBottom: `1px solid ${UMR.border}`, borderRight: `1px solid ${UMR.border}`,
        fontFamily: UMR.font, position: 'relative',
      }}
    >
      <style>{'.um-toolbar-input::placeholder{color:var(--nj-core-color-grey-600,#718492)}.um-toolbar-input:focus{border:2px solid var(--nj-core-color-blue-600,#007acd)!important}'}</style>
      {variant === 'current' ? (
        <>
          {selectionField}
          {searchField}
          {tools}
        </>
      ) : (
        <>
          {searchField}
          <Divider />
          {selectionField}
          <Divider />
          {tools}
          {geoControl && <Divider />}
          {geoControl}
        </>
      )}
      <UMToolbarIconButton icon="my_location" label="Me localiser" onClick={onLocate} />
    </div>
  );
}
